import React, { useState, useCallback, useEffect } from 'react';
import block from 'bem-cn';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Select from 'components/Select/desktop';
import Button from 'components/Button';
import InputDate from 'components/InputDate/desktop';
import Input from 'components/Input';
import CheckBox from 'components/CheckBox';

import DaysFilter from 'features/reports/view/desktop/DaysFilter';
import { actions as treeActions } from 'features/tree';

import { searchInItems } from 'features/reports/data';

import { actions } from 'features/reports/redux';

import useIfInviteHidden from 'modules/App/model/useInvitePlayer/useIfInviteHidden';

import './FilterBlock.scss';

const b = block('filter-block-cash');

const FilterBlock = ({ count, currentPage, isMobile, onPageClick, setIsOnlyTotal, isOnlyTotal }) => {
  const locale = useSelector(state => state.locale.locale);
  const dispatch = useDispatch();
  const locationRouter = useLocation();
  const ifInviteHidden = useIfInviteHidden();

  const user = new URLSearchParams(locationRouter.search).get('username') ?? '';
  const role = new URLSearchParams(locationRouter.search).get('role') ?? '';
  const searchInItemsFiltered = searchInItems.filter(s => ifInviteHidden ? !s?.isReferral : true);

  const [date, setDate] = useState({
    beginDate: new Date(dayjs().add(-1, 'day').startOf('date')),
    endDate: new Date(dayjs().startOf('date')),
  });
  const [activeDay, setActiveDay] = useState('');
  const [filters, setFilters] = useState({
    role: searchInItemsFiltered.find(item => item.value === role) ?? searchInItemsFiltered[0],
    user,
  });
  const [isDirectOnly, setIsDirectOnly] = useState(false);
  const [isHigher, setIsHigher] = useState(false);
  const [isDeposit, setIsDeposit] = useState(false);
  const [isWithdrawal, setIsWithdrawal] = useState(false);
  const [isBonus, setIsBonus] = useState(false);

  const onChangeBeginDate = value => {
    if (!isNaN(value.valueOf())) {
      setDate({ ...date, beginDate: value });
    }
    setActiveDay('');
  };

  const onChangeEndDate = value => {
    if (!isNaN(value.valueOf())) {
      setDate({ ...date, endDate: value });
    }
  };

  const handleSearchInChange = value => {
    const searchInItem = searchInItemsFiltered.find(item => item.value === value);
    setFilters({ ...filters, role: searchInItem });
  };

  const handleUserChange = useCallback(
    e => {
      setFilters({ ...filters, [e.currentTarget.name]: e.currentTarget.value });
    },
    [filters],
  );

  const handleSearch = useCallback(
    (isTotal = isOnlyTotal) => {
      const requestedData = {
        isDirectOnly,
        isHigher,
        isDeposit,
        isWithdrawal,
        role: filters.role.role,
        isReferral: filters.role.isReferral,
        username: filters.user,
        count,
        page: currentPage,
        isBonus,
        ...date,
      };
      if (isTotal) {
        dispatch(actions.getPaymentHistoryTotals(requestedData));
      } else {
        dispatch(actions.getPaymentHistory(requestedData));
      }
    },
    [
      count,
      currentPage,
      date,
      dispatch,
      filters.role.value,
      filters.user,
      isDeposit,
      isDirectOnly,
      isHigher,
      isWithdrawal,
      isOnlyTotal,
      isBonus,
    ],
  );

  useEffect(() => {
    handleSearch();
  }, [count, currentPage, handleSearch, isOnlyTotal]);

  const onClickSearch = () => {
    // filters.user.length > 0 && getTreeById(filters.user || '');
    handleSearch(true);
    dispatch(treeActions.setSearchTree(filters.user));
    setIsOnlyTotal(true);
    onPageClick(0);
  };

  useEffect(() => {
    dispatch(treeActions.setSearchTree(''));
  }, []);

  const onChangeActiveDay = useCallback(
    dayFilter => {
      setActiveDay(dayFilter);
      setDate({ ...date, beginDate: dayFilter.value, endDate: dayFilter.value2 });
    },
    [date],
  );

  return (
    <div className={b()}>
      <div className={b('header')}>{locale.filter}</div>
      <div className={b('top')}>
        <DaysFilter activeDay={activeDay} setActiveDay={onChangeActiveDay} isMobile={isMobile} />
        <div className={b('or')}>{locale.or}</div>
        <div className={b('date')}>
          <div className={b('date-row')}>
            <div className={b('title')}>{locale.dateFrom}</div>
            <div className={b('date-input')}>
              <InputDate selected={date.beginDate} onChange={onChangeBeginDate} />
            </div>
          </div>
          <div className={b('date-row')}>
            <div className={b('title')}>{locale.dateUntil}</div>
            <div className={b('date-input')}>
              <InputDate selected={date.endDate} onChange={onChangeEndDate} />
            </div>
          </div>
        </div>
      </div>

      <div className={b('bottom')}>
        <div className={b('inputs')}>
          <div className={b('input')}>
            <div className={b('title')}>{locale.agent}</div>
            <Input size="low" value={filters.user} callBack={handleUserChange} placeholder={locale.login} name="user" />
          </div>
          <div className={b('input', { type: 'select' })}>
            <div className={b('title')}>{locale.searchIn}</div>
            <Select name="role" items={searchInItemsFiltered} activeItem={filters.role} onChange={handleSearchInChange} />
          </div>
        </div>
        <div className={b('checkboxes')}>
          <div className={b('checkbox')}>
            <CheckBox checked={isDirectOnly} onChange={() => setIsDirectOnly(!isDirectOnly)} name="isDirectOnly" />
            <p className={b('checkbox-title')}>{locale.directOnly}</p>
          </div>
          <div className={b('checkbox')}>
            <CheckBox checked={isHigher} onChange={() => setIsHigher(!isHigher)} name="isHigher" />
            <p className={b('checkbox-title')}>{locale.higher}</p>
          </div>
          <div className={b('checkbox')}>
            <CheckBox checked={isDeposit} onChange={() => setIsDeposit(!isDeposit)} name="isDeposit" />
            <p className={b('checkbox-title')}>{locale.toDeposit}</p>
          </div>
          <div className={b('checkbox')}>
            <CheckBox checked={isWithdrawal} onChange={() => setIsWithdrawal(!isWithdrawal)} name="isWithdrawal" />
            <p className={b('checkbox-title')}>{locale.withdrawal}</p>
          </div>
          <div className={b('checkbox')}>
            <CheckBox checked={isBonus} onChange={() => setIsBonus(!isBonus)} name="isBonus" />
            <p className={b('checkbox-title')}>{locale.bonus}</p>
          </div>
        </div>
        <div className={b('button')}>
          <Button size="low" callBack={() => onClickSearch()} text={locale.acceptFilter} />
        </div>
      </div>
    </div>
  );
};

export default FilterBlock;
